import { T, useI18n } from '../lib/i18n'
import { ArrowRight, CalendarCheck2, KeyRound, MessagesSquare, ReceiptText, ShieldCheck, Sparkles } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { Logo } from '../components/Logo'
import { useAuth } from '../context/AuthContext'
import {
  formatInvitationCode,
  formatTeamInvitationCode,
  isValidInvitationCode,
  isValidTeamInvitationCode,
  pendingInvitation,
  pendingTeamInvitation,
  savePendingInvitation,
  savePendingTeamInvitation,
} from '../lib/invitations'

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.91h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.4Z" />
      <path fill="#34A853" d="M12 22c2.7 0 4.98-.9 6.63-2.37l-3.24-2.54c-.9.6-2.05.96-3.39.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.62A10 10 0 0 0 12 22Z" />
      <path fill="#FBBC05" d="M6.39 13.92A6.02 6.02 0 0 1 6.08 12c0-.67.11-1.32.31-1.92V7.46H3.04A10 10 0 0 0 2 12c0 1.61.39 3.13 1.04 4.54l3.35-2.62Z" />
      <path fill="#EA4335" d="M12 5.95c1.47 0 2.79.5 3.82 1.5l2.88-2.88A9.64 9.64 0 0 0 12 2a10 10 0 0 0-8.96 5.46l3.35 2.62C7.18 7.71 9.39 5.95 12 5.95Z" />
    </svg>
  )
}

function MicrosoftIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#f35325" d="M2 2h9.4v9.4H2z" /><path fill="#81bc06" d="M12.6 2H22v9.4h-9.4z" />
      <path fill="#05a6f0" d="M2 12.6h9.4V22H2z" /><path fill="#ffba08" d="M12.6 12.6H22V22h-9.4z" />
    </svg>
  )
}

function AppStoreBadge({ className }: { className: string }) {
  return (
    <a className={`app-store-badge ${className}`} href="https://apps.apple.com/us/app/tidyping/id6792330231" target="_blank" rel="noreferrer" aria-label="Download TidyPing on the App Store">
      <img src="/download-on-the-app-store.svg" alt="Download on the App Store" width="120" height="40" />
    </a>
  )
}

export function SignInPage() {
  const { session, signIn } = useAuth()
  const { t } = useI18n()
  const [params] = useSearchParams()
  const [pending, setPending] = useState<string | null>(null)
  const queryTeamInvitation = formatTeamInvitationCode(params.get('teamInvite') ?? '')
  const queryInvitation = formatInvitationCode(params.get('invite') ?? '')
  const teamInvitation = isValidTeamInvitationCode(queryTeamInvitation) ? queryTeamInvitation : pendingTeamInvitation()
  const clientInvitation = isValidInvitationCode(queryInvitation) ? queryInvitation : pendingInvitation()
  const invitation = teamInvitation ?? clientInvitation
  useEffect(() => {
    if (teamInvitation) savePendingTeamInvitation(teamInvitation)
    else if (clientInvitation) savePendingInvitation(clientInvitation)
  }, [clientInvitation, teamInvitation])
  if (session) return <Navigate to="/" replace />

  async function start(provider: 'apple' | 'google' | 'azure') {
    setPending(provider)
    try {
      await signIn(provider)
    } catch (error) {
      toast.error(error instanceof Error ? t(error.message) : t('Sign in could not start.'))
      setPending(null)
    }
  }

  return (
    <main className="auth-page">
      <section className="auth-story">
        <div className="auth-story__content">
          <Logo inverse />
          <div className="auth-story__badge"><Sparkles size={15} /> <T>{"Built for residential cleaning teams"}</T></div>
          <h1><T>{"Your schedule, clients, and conversations finally move together."}</T></h1>
          <p><T>{"TidyPing keeps every cleaning mutually confirmed and every client update in one professional workspace."}</T></p>
          <div className="auth-feature-grid">
            <div><CalendarCheck2 /><strong><T>{"Two-way confirmation"}</T></strong><span><T>{"Company first, then client."}</T></span></div>
            <div><MessagesSquare /><strong><T>{"In-app client chat"}</T></strong><span><T>{"Full history and attachments."}</T></span></div>
            <div><ReceiptText /><strong><T>{"Invoice control"}</T></strong><span><T>{"No payment setup required."}</T></span></div>
          </div>
          <AppStoreBadge className="auth-story__app-store" />
        </div>
        <div className="auth-story__orb auth-story__orb--one" />
        <div className="auth-story__orb auth-story__orb--two" />
      </section>

      <section className="auth-panel">
        <div className="auth-panel__inner">
          <div className="auth-mobile-logo"><Logo /></div>
          <span className="eyebrow"><T>{"Welcome to TidyPing"}</T></span>
          <h2><T>{"Sign in to your workspace"}</T></h2>
          <p>{t(teamInvitation ? 'Sign in with the exact work email named in your private employee invitation.' : invitation ? 'Sign in to accept your private cleaning company invitation.' : 'Use the same account on iPhone and web. If it’s new, we’ll guide you through setup.')}</p>
          <AppStoreBadge className="auth-panel__app-store" />

          {invitation && <div className="auth-invitation-ready"><KeyRound size={17} /><span><strong><T>{"Invitation ready"}</T></strong><small>{invitation}</small></span></div>}

          <div className="sso-stack">
            <button className="sso-button sso-button--apple" onClick={() => start('apple')} disabled={Boolean(pending)}>
              <span className="apple-glyph"></span><span>{t(pending === 'apple' ? 'Opening Apple…' : 'Continue with Apple')}</span><ArrowRight size={17} />
            </button>
            <button className="sso-button" onClick={() => start('google')} disabled={Boolean(pending)}>
              <GoogleIcon /><span>{t(pending === 'google' ? 'Opening Google…' : 'Continue with Google')}</span><ArrowRight size={17} />
            </button>
            <button className="sso-button" onClick={() => start('azure')} disabled={Boolean(pending)}>
              <MicrosoftIcon /><span>{t(pending === 'azure' ? 'Opening Microsoft…' : 'Continue with Microsoft')}</span><ArrowRight size={17} />
            </button>
          </div>

          <div className="auth-trust"><ShieldCheck size={17} /><span><T>{"Secure single sign-on. TidyPing never sees your provider password."}</T></span></div>
          <p className="auth-legal"><T>{"By continuing, you agree to the"}</T> <a href="/legal/terms"><T>{"Terms of Service"}</T></a> <T>{"and"}</T> <a href="/legal/privacy"><T>{"Privacy Policy"}</T></a>.</p>
        </div>
      </section>
    </main>
  )
}
